import { Plus, Trash2, X } from "lucide-react";
import type {
  FormField,
  FormFieldByKind,
  FormFieldKind,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { CrmMappingCombobox } from "./crm-mapping-combobox";
import { AddFieldButton } from "./add-field-button";

// Right-rail inline sticky field-config panel. Every change is applied via
// `onPatch`, which the canvas immediately dispatches to useUpdateField as a
// whole-field replacement (kind switches need the full discriminated shape).
// Light client-side validation only — the worker re-validates against the
// canonical Zod schema and surfaces issues as toasts.

export function FieldConfigPanel({
  field,
  onPatch,
  onDelete,
  onAddBelow,
}: {
  field: FormField;
  onPatch: (patch: Partial<FormField>) => void;
  onDelete: () => void;
  onAddBelow: (kind: FormFieldKind) => void;
}) {
  return (
    <div className="rounded-xl border bg-card">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Field settings
          </span>
          <p className="mt-0.5 truncate font-display text-sm font-semibold">
            {field.label || <em className="text-muted-foreground">Untitled</em>}
          </p>
        </div>
        <AddFieldButton onAdd={onAddBelow} ariaLabel="Add field below" />
      </header>

      <div className="space-y-4 px-4 py-4">
        <div className="space-y-1.5">
          <Label htmlFor="label">Label</Label>
          <Input
            id="label"
            value={field.label}
            onChange={(e) => onPatch({ label: e.target.value })}
            maxLength={200}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="name">Machine name</Label>
          <Input
            id="name"
            value={field.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            className="font-mono"
            maxLength={50}
          />
          <p className="text-[11px] text-muted-foreground">
            Used as the JSON key in payloads and the column name in downstream mappings.
            Lowercase letters, digits, underscores.
          </p>
        </div>

        {field.kind !== "section_heading" && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="placeholder">Placeholder</Label>
              <Input
                id="placeholder"
                value={field.placeholder ?? ""}
                onChange={(e) =>
                  onPatch({ placeholder: e.target.value || undefined })
                }
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="helpText">Help text</Label>
              <Textarea
                id="helpText"
                rows={2}
                value={field.helpText ?? ""}
                onChange={(e) =>
                  onPatch({ helpText: e.target.value || undefined })
                }
                maxLength={500}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border bg-background/40 px-3 py-2">
              <div>
                <Label htmlFor="required" className="cursor-pointer">
                  Required
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Reject submissions with this field empty.
                </p>
              </div>
              <Switch
                id="required"
                checked={field.required ?? false}
                onCheckedChange={(checked) => onPatch({ required: checked })}
              />
            </div>
          </>
        )}

        <KindSpecific field={field} onPatch={onPatch} />

        {field.kind !== "section_heading" && (
          <>
            <Separator />
            <div className="space-y-1.5">
              <Label>CRM mapping</Label>
              <CrmMappingCombobox
                value={field.crmMapping ?? null}
                onChange={(next) => onPatch({ crmMapping: next ?? undefined })}
              />
              <p className="text-[11px] text-muted-foreground">
                Push this value to a HubSpot property on submission accept.
              </p>
            </div>
          </>
        )}

        <Separator />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm(`Delete field "${field.label}"?`)) onDelete();
          }}
          className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete field
        </Button>
      </div>
    </div>
  );
}

function KindSpecific({
  field,
  onPatch,
}: {
  field: FormField;
  onPatch: (patch: Partial<FormField>) => void;
}) {
  switch (field.kind) {
    case "text":
    case "long_text":
      return <TextMaxLength field={field} onPatch={onPatch} />;
    case "number":
      return <NumberConfig field={field as FormFieldByKind<"number">} onPatch={onPatch} />;
    case "select":
      return <SelectConfig field={field as FormFieldByKind<"select">} onPatch={onPatch} />;
    case "file":
      return <FileConfig field={field as FormFieldByKind<"file">} onPatch={onPatch} />;
    case "section_heading":
      return <SectionHeadingHint />;
    default:
      return null;
  }
}

function TextMaxLength({
  field,
  onPatch,
}: {
  field: FormFieldByKind<"text"> | FormFieldByKind<"long_text">;
  onPatch: (patch: Partial<FormField>) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="maxLength">Max length</Label>
      <Input
        id="maxLength"
        type="number"
        min={1}
        value={field.maxLength ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") onPatch({ maxLength: undefined });
          else {
            const n = Number(raw);
            if (Number.isFinite(n) && n > 0) onPatch({ maxLength: Math.floor(n) });
          }
        }}
      />
    </div>
  );
}

function NumberConfig({
  field,
  onPatch,
}: {
  field: FormFieldByKind<"number">;
  onPatch: (patch: Partial<FormField>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-md border bg-background/40 px-3 py-2">
        <div>
          <Label htmlFor="integer" className="cursor-pointer">
            Integer only
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Reject decimal values.
          </p>
        </div>
        <Switch
          id="integer"
          checked={field.integer ?? true}
          onCheckedChange={(checked) => onPatch({ integer: checked })}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="min">Min</Label>
          <Input
            id="min"
            type="number"
            value={field.min ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") onPatch({ min: undefined });
              else {
                const n = Number(raw);
                if (Number.isFinite(n)) onPatch({ min: n });
              }
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="max">Max</Label>
          <Input
            id="max"
            type="number"
            value={field.max ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") onPatch({ max: undefined });
              else {
                const n = Number(raw);
                if (Number.isFinite(n)) onPatch({ max: n });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

function SelectConfig({
  field,
  onPatch,
}: {
  field: FormFieldByKind<"select">;
  onPatch: (patch: Partial<FormField>) => void;
}) {
  function setOption(index: number, key: "value" | "label", value: string) {
    const next = field.options.map((opt, i) =>
      i === index ? { ...opt, [key]: value } : opt,
    );
    onPatch({ options: next });
  }

  function deleteOption(index: number) {
    if (field.options.length <= 1) return;
    onPatch({ options: field.options.filter((_, i) => i !== index) });
  }

  function addOption() {
    const n = field.options.length + 1;
    onPatch({
      options: [
        ...field.options,
        { value: `option_${n}`, label: `Option ${n}` },
      ],
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-md border bg-background/40 px-3 py-2">
        <div>
          <Label htmlFor="multiple" className="cursor-pointer">
            Allow multiple
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Render checkboxes instead of a single-pick dropdown.
          </p>
        </div>
        <Switch
          id="multiple"
          checked={field.multiple ?? false}
          onCheckedChange={(checked) => onPatch({ multiple: checked })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Options</Label>
        <div className="space-y-1.5">
          {field.options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <Input
                value={opt.value}
                onChange={(e) => setOption(idx, "value", e.target.value)}
                placeholder="value"
                className="font-mono text-xs"
              />
              <Input
                value={opt.label}
                onChange={(e) => setOption(idx, "label", e.target.value)}
                placeholder="Label"
                className="text-xs"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Remove option"
                onClick={() => deleteOption(idx)}
                disabled={field.options.length <= 1}
                className="shrink-0 text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addOption}
          className="mt-1"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add option
        </Button>
      </div>
    </div>
  );
}

function FileConfig({
  field,
  onPatch,
}: {
  field: FormFieldByKind<"file">;
  onPatch: (patch: Partial<FormField>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="maxSizeBytes">Max size (bytes)</Label>
        <Input
          id="maxSizeBytes"
          type="number"
          min={1}
          value={field.maxSizeBytes ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") onPatch({ maxSizeBytes: undefined });
            else {
              const n = Number(raw);
              if (Number.isFinite(n) && n > 0)
                onPatch({ maxSizeBytes: Math.floor(n) });
            }
          }}
        />
        <p className="text-[11px] text-muted-foreground">
          Server cap is 50 MB. Leave blank to use the platform default.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="acceptedMimeTypes">Accepted MIME types</Label>
        <Input
          id="acceptedMimeTypes"
          placeholder="application/pdf, image/png"
          value={(field.acceptedMimeTypes ?? []).join(", ")}
          onChange={(e) => {
            const list = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            onPatch({ acceptedMimeTypes: list.length === 0 ? undefined : list });
          }}
        />
        <p className="text-[11px] text-muted-foreground">
          Comma-separated. Empty = any type accepted.
        </p>
      </div>
    </div>
  );
}

function SectionHeadingHint() {
  return (
    <div className="rounded-md border bg-background/40 px-3 py-3 text-xs text-muted-foreground">
      Section headings are layout-only — they have no input and never appear in the payload.
    </div>
  );
}
